import React from 'react';
import { Container, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { PageLoader } from '../../Components/LoadingSpinner';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useProfileUpdate } from '../../hooks/profile';
import './ProfilePage.css';

const ChangePasswordPage: React.FC = () => {
    const [user, loading, error] = useAuthState(auth);

    // Set document title
    useDocumentTitle('Ändra lösenord');

    const {
        passwordData,
        passwordLoading,
        passwordError,
        passwordSuccess,
        setPasswordData,
        changePassword,
        clearPasswordError,
        clearPasswordSuccess,
    } = useProfileUpdate({ name: '', email: '' });

    const handlePasswordChange = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user) return;
        try {
            await changePassword(
                passwordData.currentPassword,
                passwordData.newPassword,
                passwordData.confirmPassword,
                user
            );
        } catch (err) {
            // Error handled by hook
        }
    };

    if (loading) {
        return (
            <div className="profile-page">
                <PageLoader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="profile-page">
                <Container className="profile-page-container">
                    <Alert variant="danger">
                        <Alert.Heading>Fel vid laddning</Alert.Heading>
                        <p>{error.message}</p>
                    </Alert>
                </Container>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="profile-page">
                <Container className="profile-page-container">
                    <Alert variant="warning">
                        <Alert.Heading>Inte inloggad</Alert.Heading>
                        <p>Du måste vara inloggad för att komma åt denna sida.</p>
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <div className="profile-page">
            <Container className="profile-page-container">
                <div className="profile-page-header">
                    <h1 className="page-title">Ändra lösenord</h1>
                    <p className="profile-page-description">Uppdatera ditt lösenord</p>
                </div>

                <Card className="dropshadow" style={{ maxWidth: '500px' }}>
                    <Card.Body>
                        {passwordError && (
                            <Alert variant="danger" dismissible onClose={clearPasswordError}>
                                {passwordError}
                            </Alert>
                        )}
                        {passwordSuccess && (
                            <Alert variant="success" dismissible onClose={clearPasswordSuccess}>
                                {passwordSuccess}
                            </Alert>
                        )}

                        <Form onSubmit={handlePasswordChange}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nuvarande lösenord</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={passwordData.currentPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                    placeholder="Ange nuvarande lösenord"
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Nytt lösenord</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={passwordData.newPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                    placeholder="Ange nytt lösenord"
                                    required
                                    minLength={6}
                                />
                                <Form.Text className="text-muted">
                                    Minst 6 tecken
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Bekräfta nytt lösenord</Form.Label>
                                <Form.Control
                                    type="password"
                                    value={passwordData.confirmPassword}
                                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                    placeholder="Bekräfta nytt lösenord"
                                    required
                                    minLength={6}
                                />
                            </Form.Group>

                            <Button
                                variant="primary"
                                type="submit"
                                disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                            >
                                {passwordLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Ändrar...
                                    </>
                                ) : (
                                    'Ändra lösenord'
                                )}
                            </Button>
                        </Form>
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
};

export default ChangePasswordPage;
